import { Component } from '@verndale/core';
import { buildBrightcoveEmbed, initBrightcovePlayers, parseBrightcoveIframeUrl } from '../helpers';

const INITIALIZED_ATTR = 'data-content-video-initialized';

class ContentVideo extends Component {
  setupDefaults() {
    this.dom = {
      image: this.el.querySelector<HTMLElement>('.content-video__image')
    };
  }

  constructor(el: HTMLElement) {
    super(el);

    // Blocks are reachable from both autoInit and the data-module registry
    if (this.el.hasAttribute(INITIALIZED_ATTR)) {
      return;
    }

    this.el.setAttribute(INITIALIZED_ATTR, 'true');
    this.unwrapTrigger();
    this.renderPlayer();
  }

  /**
   * Swaps the CMS button wrapper for a plain element, since a player nested in a
   * button is invalid markup and never receives the click
   */
  unwrapTrigger() {
    const trigger = this.el.querySelector<HTMLButtonElement>('button.content-video__container');

    if (!trigger) {
      return;
    }

    const wrapper = document.createElement('div');
    wrapper.className = trigger.className;
    wrapper.append(...Array.from(trigger.childNodes));
    trigger.replaceWith(wrapper);
  }

  /**
   * Replaces the iframe embed with an in-page player, which is both directly
   * playable and visible to the Brightcove GTM plugin
   */
  renderPlayer() {
    const image = this.dom.image as HTMLElement | null;

    // Markup that already ships an in-page embed is left for Brightcove to boot.
    // Covers it before and after init, since video.js swaps the tag for a wrapper.
    if (!image || image.querySelector('video, video-js, .video-js')) {
      return;
    }

    const iframe = image.querySelector<HTMLIFrameElement>('iframe');
    // The CMS ships no data attributes, so the ids come from the iframe URL
    const parsed = parseBrightcoveIframeUrl(iframe?.getAttribute('src') || '');
    const accountId = this.el.dataset.accountId || parsed?.accountId;
    const videoId = this.el.dataset.videoId || parsed?.videoId;

    // Without ids there is nothing better to offer, so the iframe stays put
    if (!accountId || !videoId) {
      return;
    }

    iframe?.remove();
    image.insertAdjacentHTML(
      'afterbegin',
      buildBrightcoveEmbed({
        accountId,
        videoId,
        player: this.el.dataset.player || parsed?.player,
        embed: this.el.dataset.embed || parsed?.embed
      })
    );

    const heading = this.el.querySelector('h1, h2, h3, h4, h5, h6')?.textContent?.trim();

    if (heading) {
      image.querySelector('video')?.setAttribute('aria-label', heading);
    }

    initBrightcovePlayers(image);
  }

  /**
   * Boots blocks rendered by the CMS, which arrive without a data-module hook
   */
  static autoInit() {
    document
      .querySelectorAll<HTMLElement>(`.content-video:not([${INITIALIZED_ATTR}])`)
      .forEach(element => new ContentVideo(element));
  }
}

export default ContentVideo;
